import FormControl from '@mui/material/FormControl';
import type { SxProps, Theme } from '@mui/material/styles';

import AutocompleteWithList from 'shared/molecules/AutocompleteWithList';
import { AUTOCOMPLETEWITHLIST_TYPE } from 'shared/const';
import type { BackgroundThemes } from 'shared/const';

import FormLabel from '../components/FormLabel';
import type { FormFieldProperties } from '../FormInput';

interface AutocompleteWithListItem {
  key: string;
  title: string;
}

interface FormAutocompleteWithListProps {
  disabled?: boolean;
  errors?: Record<string, string>;
  id: string;
  infoText?: string;
  label: string;
  name: string;
  onAdd?: (key: string) => void;
  onDelete?: (key: string) => void;
  properties?: Record<string, FormFieldProperties>;
  required?: boolean;
  selectedItems?: AutocompleteWithListItem[];
  sx?: SxProps<Theme>;
  theme?: BackgroundThemes;
  values?: Record<string, unknown>;
}

const FormAutocompleteWithList = ({
  disabled: disabledProp,
  id,
  infoText,
  label,
  name,
  onAdd,
  onDelete,
  properties = {},
  required: requiredProp,
  selectedItems = [],
  sx = {},
  theme: themeLayout = 'dark',
  values = {},
  ...props
}: FormAutocompleteWithListProps) => {
  const { disabled, required } = properties[name] || {};

  const onChangeHandler = (values: AutocompleteWithListItem[]) => {
    if (values.length > selectedItems.length) {
      const newItem = values.find(
        (value) => !selectedItems.find((item) => item.key === value.key)
      );
      if (newItem && onAdd) {
        onAdd(newItem.key);
      }
    } else if (values.length < selectedItems.length) {
      const removedItem = selectedItems.find(
        (item) => !values.find((value) => value.key === item.key)
      );
      if (removedItem && onDelete) {
        onDelete(removedItem.key);
      }
    }
  };

  return (
    <FormControl
      disabled={disabledProp === undefined ? disabled : disabledProp}
      fullWidth
      required={requiredProp === undefined ? required : requiredProp}
      sx={sx}
    >
      <FormLabel
        id={id}
        infoText={infoText}
        label={label}
        theme={themeLayout}
      />
      <AutocompleteWithList
        id={id}
        name={name}
        onChange={onChangeHandler}
        selectedItems={selectedItems}
        theme={themeLayout}
        type={AUTOCOMPLETEWITHLIST_TYPE.FORM}
        value={values[name]}
        disabled={disabledProp}
        {...props}
      />
    </FormControl>
  );
};

export default FormAutocompleteWithList;
export type { FormAutocompleteWithListProps, AutocompleteWithListItem };
